<!-- Loading State -->
<div *ngIf="isLoading" class="d-flex justify-content-center align-items-center" style="min-height: 400px;">
  <div class="spinner-border text-primary" role="status">
    <span class="visually-hidden">Loading...</span>
  </div>
</div>

<!-- Vehicle Detail Content -->
<div *ngIf="!isLoading && vehicleData" class="vehicle-detail-container">

  <!-- Header Section -->
  <div class="card mb-4">
    <div class="card-body">
      <div class="d-flex justify-content-between align-items-start mb-4">
        <div>
          <button type="button" class="btn btn-light btn-sm mb-3" (click)="goBack()">
            <i class="ki-duotone ki-arrow-left fs-2">
              <span class="path1"></span>
              <span class="path2"></span>
            </i>
            Back to Search
          </button>
          <h1 class="fw-bold text-gray-900 mb-2">
            {{ vehicleData.company_name }} {{ vehicleData.name }} {{ vehicleData.year }}
          </h1>
          <div class="d-flex align-items-center gap-4 text-muted">
            <span><i class="ki-duotone ki-calendar fs-3 me-1"><span class="path1"></span><span
                  class="path2"></span></i>Lot: {{ vehicleData.lot_no }}</span>
            <span><i class="ki-duotone ki-car fs-3 me-1"><span class="path1"></span><span class="path2"></span><span
                  class="path3"></span></i>{{ vehicleData.inventory_no }}</span>
            <span class="badge" [class]="vehicleData.is_sold ? 'badge-danger' : 'badge-success'">
              {{ vehicleData.is_sold ? 'SOLD' : 'AVAILABLE' }}
            </span>
          </div>
        </div>
        <div class="text-end">
          <div class="fs-2 fw-bold text-primary mb-1">¥{{ formatPrice(vehicleData.price) }}</div>
          <div class="text-muted">Current Price</div>
        </div>
      </div>

      <!-- Auction Info Bar -->
      <div class="auction-info-bar p-3 bg-light-primary rounded">
        <div class="row align-items-center">
          <div class="col-md-8">
            <div class="d-flex align-items-center">
              <i class="ki-duotone ki-time fs-1 text-primary me-3">
                <span class="path1"></span>
                <span class="path2"></span>
              </i>
              <div>
                <div class="fw-bold text-gray-900">Auction Date</div>
                <div class="text-primary">{{ formatAuctionDate(vehicleData.auction_date) }}</div>
              </div>
            </div>
          </div>
          <div class="col-md-4 text-end">


            <div class="fw-bold text-gray-700 mb-1">
              <i class="ki-duotone ki-home fs-1 text-primary me-3">
                <span class="path1"></span>
                <span class="path2"></span>
              </i>
              Hall Name
            </div>
            <div class="text-muted small">{{ vehicleData?.hall_name || 'Auction Hall' }}</div>

          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Main Content Row -->
  <div class="row g-4">

    <!-- Left Column - Images and Details -->
    <div class="col-lg-8">

      <!-- Vehicle Information Table -->
      <div class="card mb-4">
        <div class="card-body p-0">
          <div class="table-responsive">
            <table class="table table-bordered mb-0 vehicle-info-table">
              <tbody>
                <!-- First Row -->
                <tr>
                  <td class="info-label">Auction Date</td>
                  <td class="info-value">{{ formatAuctionDate(vehicleData.auction_date) }}</td>
                  <td class="info-label">Auction Time</td>
                  <td class="info-value">{{ formatAuctionTime(vehicleData.auction_date) }}</td>
                  <td class="info-label">Lot Number</td>
                  <td class="info-value">{{ vehicleData.lot_no }}</td>
                  <td class="info-label">Auction Hall</td>
                  <td class="info-value">{{ vehicleData?.hall_name || '-' }}</td>
                </tr>
                <!-- Second Row -->
                <tr>
                  <td class="info-label">Year</td>
                  <td class="info-value">{{ vehicleData.year }}</td>
                  <td class="info-label">Make</td>
                  <td class="info-value">{{ vehicleData.company_name }}</td>
                  <td class="info-label">Model</td>
                  <td class="info-value">{{ vehicleData.name }}</td>
                  <td class="info-label">Grade</td>
                  <td class="info-value">{{ vehicleData.parsed_data?.['Grade'] || '-' }}</td>
                </tr>
                <!-- Third Row -->
                <tr>
                  <td class="info-label">Engine</td>
                  <td class="info-value">{{ vehicleData.parsed_data?.['Engine Size'] || vehicleData.parsed_data?.['Engine'] || '-' }}</td>
                  <td class="info-label">Mileage</td>
                  <td class="info-value">{{ vehicleData.mileage | number }}{{ vehicleData.mileage ? ' KM' : 'N/A' }}</td>
                  <td class="info-label">Color</td>
                  <td class="info-value">{{ vehicleData.parsed_data?.['Exterior Color'] || vehicleData.parsed_data?.['Color'] || 'BLACK' }}</td>
                  <td class="info-label">Transmission</td>
                  <td class="info-value">{{ vehicleData.parsed_data?.['Transmission'] || '-' }}</td>
                </tr>
                <!-- Fourth Row -->
                <tr>
                  <td class="info-label">Chassis No.</td>
                  <td class="info-value" colspan="3">{{ vehicleData.vin_no || '-' }}</td>
                  <td class="info-label">Current Price</td>
                  <td class="info-value price-highlight" colspan="3">
                    <span class="fw-bold text-primary fs-5">¥{{ formatPrice(vehicleData.price) }}</span>
                    <span class="badge badge-light-success ms-2" *ngIf="!vehicleData.is_sold">Available</span>
                    <span class="badge badge-danger ms-2" *ngIf="vehicleData.is_sold">SOLD</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <!-- Image Gallery -->
      <div class="card mb-4">
        <div class="card-body p-0">
          <!-- Main Image with Navigation -->
          <div class="image-gallery-wrapper">
            <!-- Left Navigation Arrow -->
            <button *ngIf="vehicleData.pictures && vehicleData.pictures.length > 1" type="button"
              class="btn btn-light btn-sm nav-arrow-btn nav-arrow-left"
              (click)="previousImage()" [disabled]="selectedImageIndex === 0"
              title="Previous Image">
              <span class="arrow-icon">‹</span>
            </button>

            <!-- Main Image Container -->
            <div class="main-image-container position-relative">
              <img [src]="getMainImage()" [alt]="vehicleData.company_name + ' ' + vehicleData.name"
                class="main-image w-100">

              <!-- Image Counter -->
              <div class="position-absolute bottom-0 end-0 bg-dark bg-opacity-75 text-white px-3 py-1 m-3 rounded">
                {{ selectedImageIndex + 1 }} / {{ vehicleData.pictures?.length || 1 }}
              </div>
            </div>

            <!-- Right Navigation Arrow -->
            <button *ngIf="vehicleData.pictures && vehicleData.pictures.length > 1" type="button"
              class="btn btn-light btn-sm nav-arrow-btn nav-arrow-right"
              (click)="nextImage()"
              [disabled]="selectedImageIndex === vehicleData.pictures.length - 1"
              title="Next Image">
              <span class="arrow-icon">›</span>
            </button>
          </div>

          <!-- Thumbnail Gallery -->
          <div *ngIf="vehicleData.pictures && vehicleData.pictures.length > 1" class="thumbnail-gallery p-3">
            <div class="d-flex gap-2 overflow-auto">
              <img *ngFor="let image of vehicleData.pictures; let i = index" [src]="image" [alt]="'Image ' + (i + 1)"
                class="thumbnail" [class.active]="i === selectedImageIndex" (click)="selectImage(i)"
                style="width: 80px; height: 60px; object-fit: cover; cursor: pointer; border-radius: 4px;">
            </div>
          </div>
        </div>
      </div>

      <!-- Vehicle Details Tabs -->
      <div class="card">
        <div class="card-header border-0 pt-6">
          <ul class="nav nav-stretch nav-line-tabs nav-line-tabs-2x border-transparent fs-5 fw-bold">
            <li class="nav-item">
              <a class="nav-link text-active-primary ms-0 me-10 py-3" [class.active]="activeTab === 'overview'"
                (click)="setActiveTab('overview')" role="button">
                Overview
              </a>
            </li>
            <li class="nav-item">
              <a class="nav-link text-active-primary ms-0 me-10 py-3" [class.active]="activeTab === 'specifications'"
                (click)="setActiveTab('specifications')" role="button">
                Specifications
              </a>
            </li>
            <li class="nav-item">
              <a class="nav-link text-active-primary ms-0 me-10 py-3" [class.active]="activeTab === 'features'"
                (click)="setActiveTab('features')" role="button">
                Features
              </a>
            </li>
            <li class="nav-item">
              <a class="nav-link text-active-primary ms-0 me-10 py-3" [class.active]="activeTab === 'inspection'"
                (click)="setActiveTab('inspection')" role="button">
                Inspection
              </a>
            </li>
          </ul>
        </div>

        <div class="card-body">
          <!-- Overview Tab -->
          <div *ngIf="activeTab === 'overview'" class="tab-content">
            <div class="row g-4">
              <div class="col-md-6">
                <div class="info-group">
                  <h6 class="fw-bold text-gray-800 mb-3">Basic Information</h6>
                  <div class="info-item">
                    <span class="label">Make:</span>
                    <span class="value">{{ vehicleData.company_name }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">Model:</span>
                    <span class="value">{{ vehicleData.name }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">Year:</span>
                    <span class="value">{{ vehicleData.year }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">Color:</span>
                    <span class="value">{{ vehicleData.color }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">Mileage:</span>
                    <span class="value">{{ formatPrice(vehicleData.mileage) }} km</span>
                  </div>
                </div>
              </div>

              <div class="col-md-6">
                <div class="info-group">
                  <h6 class="fw-bold text-gray-800 mb-3">Engine & Performance</h6>
                  <div class="info-item">
                    <span class="label">Engine:</span>
                    <span class="value">{{ vehicleData.engine_capacity }}cc</span>
                  </div>
                  <div class="info-item">
                    <span class="label">Fuel Type:</span>
                    <span class="value">{{ vehicleData.fuel_type }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">Transmission:</span>
                    <span class="value">{{ vehicleData.transmission }}</span>
                  </div>
                  <div class="info-item" *ngIf="vehicleData.doors">
                    <span class="label">Doors:</span>
                    <span class="value">{{ vehicleData.doors }}</span>
                  </div>
                  <div class="info-item" *ngIf="vehicleData.vin_no">
                    <span class="label">VIN:</span>
                    <span class="value">{{ vehicleData.vin_no }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Additional Info -->
            <div class="row g-4 mt-2" *ngIf="vehicleData.parsed_data">
              <div class="col-12">
                <div class="info-group">
                  <h6 class="fw-bold text-gray-800 mb-3">Additional Information</h6>
                  <div class="row">
                    <div class="col-md-6" *ngIf="vehicleData.parsed_data[' car history ']">
                      <div class="info-item">
                        <span class="label">Car History:</span>
                        <span class="value">{{ vehicleData.parsed_data[' car history '] }}</span>
                      </div>
                    </div>
                    <div class="col-md-6" *ngIf="vehicleData.parsed_data[' condition ']">
                      <div class="info-item">
                        <span class="label">Condition:</span>
                        <span class="value">{{ vehicleData.parsed_data[' condition '] }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Specifications Tab -->
          <div *ngIf="activeTab === 'specifications'" class="tab-content">
            <div class="row g-4">
              <div class="col-md-4" *ngIf="vehicleData.car_length || vehicleData.car_width || vehicleData.car_height">
                <div class="info-group">
                  <h6 class="fw-bold text-gray-800 mb-3">Dimensions</h6>
                  <div class="info-item" *ngIf="vehicleData.car_length">
                    <span class="label">Length:</span>
                    <span class="value">{{ vehicleData.car_length }} mm</span>
                  </div>
                  <div class="info-item" *ngIf="vehicleData.car_width">
                    <span class="label">Width:</span>
                    <span class="value">{{ vehicleData.car_width }} mm</span>
                  </div>
                  <div class="info-item" *ngIf="vehicleData.car_height">
                    <span class="label">Height:</span>
                    <span class="value">{{ vehicleData.car_height }} mm</span>
                  </div>
                </div>
              </div>

              <div class="col-md-4">
                <div class="info-group">
                  <h6 class="fw-bold text-gray-800 mb-3">Engine Details</h6>
                  <div class="info-item">
                    <span class="label">Engine Capacity:</span>
                    <span class="value">{{ vehicleData.engine_capacity }}cc</span>
                  </div>
                  <div class="info-item">
                    <span class="label">Fuel Type:</span>
                    <span class="value">{{ vehicleData.fuel_type }}</span>
                  </div>
                  <div class="info-item" *ngIf="vehicleData.parsed_data[' fuel ']">
                    <span class="label">Fuel System:</span>
                    <span class="value">{{ vehicleData.parsed_data[' fuel '] }}</span>
                  </div>
                </div>
              </div>

              <div class="col-md-4" *ngIf="vehicleData.inspection_expiry_date">
                <div class="info-group">
                  <h6 class="fw-bold text-gray-800 mb-3">Inspection</h6>
                  <div class="info-item">
                    <span class="label">Expiry Date:</span>
                    <span class="value">{{ vehicleData.inspection_expiry_date | date:'mediumDate' }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Features Tab -->
          <div *ngIf="activeTab === 'features'" class="tab-content">
            <!-- Header with Analyze Button -->
            <div *ngIf="!hasInspectionAnalysis()" class="d-flex justify-content-between align-items-center mb-4">
              <h6 class="fw-bold text-gray-800 mb-0">Vehicle Features</h6>
              <!-- Analyze Sheet Button - Only show if inspection_analysis doesn't exist -->
              <button
                type="button"
                class="btn btn-primary btn-sm"
                (click)="analyzeCarSheet()"
                [disabled]="isAnalyzingSheet">
                <i class="ki-duotone ki-chart-simple fs-3 me-1">
                  <span class="path1"></span>
                  <span class="path2"></span>
                  <span class="path3"></span>
                  <span class="path4"></span>
                </i>
                <span *ngIf="!isAnalyzingSheet">Analyze Sheet</span>
                <span *ngIf="isAnalyzingSheet">Analyzing...</span>
              </button>
            </div>

            <!-- Message when no analysis data - Show if inspection_analysis doesn't exist -->
            <div *ngIf="!hasInspectionAnalysis()" class="text-center py-5">
              <i class="ki-duotone ki-information-5 fs-5x text-primary mb-4">
                <span class="path1"></span>
                <span class="path2"></span>
                <span class="path3"></span>
              </i>
              <h5 class="fw-bold text-gray-800 mb-3">No Feature Analysis Available</h5>
              <p class="text-muted mb-0">
                Click the "Analyze Sheet" button above to analyze the inspection sheet and view sales points and features here.
              </p>
            </div>

            <!-- Show content when inspection analysis exists -->
            <div *ngIf="hasInspectionAnalysis()">
              <!-- Options from parsed data -->
              <div class="row g-4 mt-2" *ngIf="getFeaturesList().length > 0">
                <div class="col-12">
                  <div class="info-group">
                    <h6 class="fw-bold text-gray-800 mb-3">Additional Options</h6>
                    <div class="d-flex flex-wrap gap-2">
                      <span *ngFor="let feature of getFeaturesList()" class="badge badge-light-primary fs-7 fw-semibold">
                        {{ feature }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Inspection Analysis Features Section -->
              <!-- Sales Points from Inspection Analysis -->
              <div class="row g-4 mt-2" *ngIf="vehicleData.inspection_analysis && vehicleData.inspection_analysis.sales_points && vehicleData.inspection_analysis.sales_points.length > 0">
                <div class="col-12">
                  <div class="info-group">
                    <h6 class="fw-bold text-gray-800 mb-3">
                      <i class="ki-duotone ki-star fs-2 text-warning me-2">
                        <span class="path1"></span>
                        <span class="path2"></span>
                      </i>
                      Sales Points
                    </h6>
                    <div class="d-flex flex-wrap gap-2">
                      <span *ngFor="let point of vehicleData.inspection_analysis.sales_points"
                            class="badge badge-light-primary fs-6 fw-semibold py-2 px-3">
                        {{ point }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Features from Inspection Analysis -->
              <div class="row g-4 mt-2" *ngIf="vehicleData.inspection_analysis && vehicleData.inspection_analysis.features && vehicleData.inspection_analysis.features.length > 0">
                <div class="col-12">
                  <div class="info-group">
                    <h6 class="fw-bold text-gray-800 mb-3">
                      <i class="ki-duotone ki-check-square fs-2 text-success me-2">
                        <span class="path1"></span>
                        <span class="path2"></span>
                      </i>
                      Analyzed Features
                    </h6>
                    <div class="d-flex flex-wrap gap-2">
                      <span *ngFor="let feature of vehicleData.inspection_analysis.features"
                            class="badge badge-light-success fs-6 fw-semibold py-2 px-3">
                        <i class="ki-duotone ki-check-circle fs-5 me-1">
                          <span class="path1"></span>
                          <span class="path2"></span>
                        </i>
                        {{ feature }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Inspection Tab -->
          <div *ngIf="activeTab === 'inspection'" class="tab-content">
            <div class="inspection-sheet-container">
              <div class="d-flex justify-content-between align-items-center mb-4">
                <h6 class="fw-bold text-gray-800 mb-0">Inspection Sheet</h6>
                <!-- Analyze Sheet Button - Only show if inspection_analysis doesn't exist -->
                <button
                  *ngIf="!hasInspectionAnalysis()"
                  type="button"
                  class="btn btn-primary btn-sm"
                  (click)="analyzeCarSheet()"
                  [disabled]="isAnalyzingSheet">
                  <i class="ki-duotone ki-chart-simple fs-3 me-1">
                    <span class="path1"></span>
                    <span class="path2"></span>
                    <span class="path3"></span>
                    <span class="path4"></span>
                  </i>
                  <span *ngIf="!isAnalyzingSheet">Analyze Sheet</span>
                  <span *ngIf="isAnalyzingSheet">Analyzing...</span>
                </button>
              </div>

              <!-- Message when no analysis data - Show if inspection_analysis doesn't exist -->
              <div *ngIf="!hasInspectionAnalysis()" class="text-center py-5">
                <i class="ki-duotone ki-information-5 fs-5x text-primary mb-4">
                  <span class="path1"></span>
                  <span class="path2"></span>
                  <span class="path3"></span>
                </i>
                <h5 class="fw-bold text-gray-800 mb-3">No Inspection Analysis Available</h5>
                <p class="text-muted mb-0">
                  Click the "Analyze Sheet" button above to analyze the inspection sheet and view detailed information.
                </p>
              </div>

              <!-- Inspection Analysis Data - Show if exists -->
              <div *ngIf="hasInspectionAnalysis()" class="inspection-analysis mb-4">
                <div class="card border border-primary">
                  <div class="card-header bg-light-primary">
                    <h5 class="card-title mb-0 text-primary">
                      <i class="ki-duotone ki-verify fs-2 me-2">
                        <span class="path1"></span>
                        <span class="path2"></span>
                      </i>
                      Inspection Analysis
                    </h5>
                  </div>
                  <div class="card-body">
                    <!-- Basic Information -->
                    <div class="row g-4 mb-4">
                      <div class="col-md-6">
                        <h6 class="fw-bold text-gray-800 mb-3">Vehicle Information</h6>
                        <div class="info-grid">
                          <div class="info-item mb-2">
                            <span class="label fw-semibold text-gray-600">Inspection Number:</span>
                            <span class="value text-gray-800">{{ vehicleData.inspection_analysis?.inspection_number }}</span>
                          </div>
                          <div class="info-item mb-2">
                            <span class="label fw-semibold text-gray-600">Year:</span>
                            <span class="value text-gray-800">{{ vehicleData.inspection_analysis?.year }}</span>
                          </div>
                          <div class="info-item mb-2">
                            <span class="label fw-semibold text-gray-600">Make:</span>
                            <span class="value text-gray-800">{{ vehicleData.inspection_analysis?.make }}</span>
                          </div>
                          <div class="info-item mb-2">
                            <span class="label fw-semibold text-gray-600">Model:</span>
                            <span class="value text-gray-800">{{ vehicleData.inspection_analysis?.model }}</span>
                          </div>
                          <div class="info-item mb-2">
                            <span class="label fw-semibold text-gray-600">Type:</span>
                            <span class="value text-gray-800">{{ vehicleData.inspection_analysis?.type }}</span>
                          </div>
                          <div class="info-item mb-2">
                            <span class="label fw-semibold text-gray-600">Engine Capacity:</span>
                            <span class="value text-gray-800">{{ vehicleData.inspection_analysis?.engine_capacity }}</span>
                          </div>
                          <div class="info-item mb-2">
                            <span class="label fw-semibold text-gray-600">Fuel Type:</span>
                            <span class="value text-gray-800">{{ vehicleData.inspection_analysis?.fuel_type }}</span>
                          </div>
                          <div class="info-item mb-2">
                            <span class="label fw-semibold text-gray-600">Chassis No:</span>
                            <span class="value text-gray-800">{{ vehicleData.inspection_analysis?.chassis_no }}</span>
                          </div>
                          <div class="info-item mb-2" *ngIf="vehicleData.inspection_analysis?.vehicle_number">
                            <span class="label fw-semibold text-gray-600">Vehicle Number:</span>
                            <span class="value text-gray-800">{{ vehicleData.inspection_analysis?.vehicle_number }}</span>
                          </div>
                        </div>
                      </div>

                      <div class="col-md-6">
                        <h6 class="fw-bold text-gray-800 mb-3">Inspection Details</h6>
                        <div class="info-grid">
                          <div class="info-item mb-2">
                            <span class="label fw-semibold text-gray-600">Odometer:</span>
                            <span class="value text-gray-800">{{ vehicleData.inspection_analysis?.odometer }}</span>
                          </div>
                          <div class="info-item mb-2">
                            <span class="label fw-semibold text-gray-600">Grade:</span>
                            <span class="badge badge-light-success">{{ vehicleData.inspection_analysis?.auction_grade_point }}</span>
                          </div>
                          <div class="info-item mb-2">
                            <span class="label fw-semibold text-gray-600">Interior Grade:</span>
                            <span class="badge badge-light-success">{{ vehicleData.inspection_analysis?.interior_grade }}</span>
                          </div>
                          <div class="info-item mb-2">
                            <span class="label fw-semibold text-gray-600">Exterior Grade:</span>
                            <span class="badge badge-light-success">{{ vehicleData.inspection_analysis?.exterior_grade }}</span>
                          </div>
                          <div class="info-item mb-2">
                            <span class="label fw-semibold text-gray-600">Condition:</span>
                            <span class="badge badge-light-info">{{ vehicleData.inspection_analysis?.condition }}</span>
                          </div>
                          <div class="info-item mb-2" *ngIf="vehicleData.inspection_analysis?.inspection_date">
                            <span class="label fw-semibold text-gray-600">Inspection Date:</span>
                            <span class="value text-gray-800">{{ vehicleData.inspection_analysis?.inspection_date }}</span>
                          </div>
                        </div>

                        <h6 class="fw-bold text-gray-800 mb-3 mt-4">Dimensions</h6>
                        <div class="info-grid">
                          <div class="info-item mb-2">
                            <span class="label fw-semibold text-gray-600">Length:</span>
                            <span class="value text-gray-800">{{ vehicleData.inspection_analysis?.dimensions?.length }}</span>
                          </div>
                          <div class="info-item mb-2">
                            <span class="label fw-semibold text-gray-600">Width:</span>
                            <span class="value text-gray-800">{{ vehicleData.inspection_analysis?.dimensions?.width }}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Registration Details -->
                    <div class="row g-4 mb-4">
                      <div class="col-12">
                        <h6 class="fw-bold text-gray-800 mb-3">Registration Details</h6>
                        <div class="row">
                          <div class="col-md-6">
                            <div class="info-item mb-2">
                              <span class="label fw-semibold text-gray-600">Registration Date:</span>
                              <span class="value text-gray-800">{{ vehicleData.inspection_analysis?.registration_details?.registration_date }}</span>
                            </div>
                            <div class="info-item mb-2">
                              <span class="label fw-semibold text-gray-600">Location:</span>
                              <span class="value text-gray-800">{{ vehicleData.inspection_analysis?.registration_details?.location }}</span>
                            </div>
                          </div>
                          <div class="col-md-6">
                            <div class="info-item mb-2">
                              <span class="label fw-semibold text-gray-600">Seating Capacity:</span>
                              <span class="value text-gray-800">{{ vehicleData.inspection_analysis?.registration_details?.seating_capacity }}</span>
                            </div>
                            <div class="info-item mb-2" *ngIf="vehicleData.inspection_analysis?.registration_details?.weight">
                              <span class="label fw-semibold text-gray-600">Weight:</span>
                              <span class="value text-gray-800">{{ vehicleData.inspection_analysis?.registration_details?.weight }}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Inspection Notes -->
                    <div class="row g-4" *ngIf="vehicleData.inspection_analysis && vehicleData.inspection_analysis.inspection_notes && vehicleData.inspection_analysis.inspection_notes.length > 0">
                      <div class="col-12">
                        <h6 class="fw-bold text-gray-800 mb-3">Inspection Notes</h6>
                        <div class="bg-light-warning border border-warning rounded p-3">
                          <ul class="mb-0 ps-4">
                            <li *ngFor="let note of vehicleData.inspection_analysis.inspection_notes"
                                class="text-gray-700 mb-1">
                              {{ note }}
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Right Column - Bid Section -->
    <div class="col-lg-4">

      <!-- Make Bid Card -->
      <div class="card">
        <div class="card-header border-0 pt-6">
          <div class="card-title">
            <h3 class="fw-bold text-gray-900 m-0">
              <i class="ki-duotone ki-dollar fs-2 text-primary me-2">
                <span class="path1"></span>
                <span class="path2"></span>
                <span class="path3"></span>
              </i>
              {{ isAucnetOrOnePrice() ? 'Add to Stock' : 'Make a Bid' }}
            </h3>
          </div>
        </div>

        <div class="card-body">
          <!-- Current Price Display -->
          <div class="price-display mb-6">
            <div class="d-flex justify-content-between align-items-center p-4 bg-light-primary rounded">
              <div>
                <div class="text-muted small">Current Price</div>
                <div class="fs-2 fw-bold text-primary">¥{{ formatPrice(vehicleData.price) }}</div>
              </div>
              <div class="text-end">
                <div class="text-muted small">Status</div>
                <div class="badge" [class]="vehicleData.is_sold ? 'badge-danger' : 'badge-success'">
                  {{ vehicleData.is_sold ? 'SOLD' : 'AVAILABLE' }}
                </div>
              </div>
            </div>
          </div>

          <!-- For Aucnet or One-Price: Show Add to Real Stock Button -->
          <ng-container *ngIf="isAucnetOrOnePrice()">
            <!-- Show action buttons if vehicle is not added to stock -->
            <ng-container *ngIf="!vehicleData.is_added_to_stock || !vehicleData.vehicle_id">
              <button
                class="btn btn-lg w-100 btn-primary"
                (click)="addToRealStock()"
                [disabled]="isAddingToStock">
                <span *ngIf="isAddingToStock" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                {{ isAddingToStock ? 'Adding...' : 'Add To Real Stock' }}
              </button>
            </ng-container>

            <!-- Show view stock link if vehicle is already added to stock -->
            <ng-container *ngIf="vehicleData.is_added_to_stock && vehicleData.vehicle_id">
              <div class="text-center p-4">
                <i class="ki-duotone ki-check-circle fs-3x text-success mb-3">
                  <span class="path1"></span>
                  <span class="path2"></span>
                </i>
                <h5 class="fw-bold text-success mb-3">Already Added to Stock</h5>
                <a
                  [routerLink]="['/vehicles/vehicle-manager/edit', vehicleData.vehicle_id]"
                  class="btn btn-light-success">
                  <i class="ki-duotone ki-eye fs-6 me-1">
                    <span class="path1"></span>
                    <span class="path2"></span>
                    <span class="path3"></span>
                  </i>
                  View the Stock Here
                </a>
              </div>
            </ng-container>
          </ng-container>

          <!-- For Alaedo: Show Bid Form -->
          <ng-container *ngIf="isAlaedo()">
            <!-- Minimum Bid Notice -->
            <div class="alert alert-info mb-4" *ngIf="!vehicleData.is_sold">
              <i class="ki-duotone ki-information-5 fs-2 text-info me-2">
                <span class="path1"></span>
                <span class="path2"></span>
                <span class="path3"></span>
              </i>
              <strong>Minimum Bid Amount should be {{ formatPrice(vehicleData.service_data?.minPrice || 0)}} JPY</strong>
            </div>

            <!-- Bid Form -->
            <form [formGroup]="bidForm" (ngSubmit)="onSubmitBid()" *ngIf="!vehicleData.is_sold">

            <!-- Bid Amount -->
            <div class="fv-row mb-4">
              <label class="form-label fw-semibold text-gray-800 required">Bid Amount</label>
              <input type="number"
                     class="form-control form-control-lg"
                     [class.is-invalid]="isFieldInvalid('bidAmount')"
                     formControlName="bidAmount"
                     placeholder="Enter bid amount">
              <div *ngIf="isFieldInvalid('bidAmount')" class="invalid-feedback">
                {{ getFieldError('bidAmount') }}
              </div>
            </div>

            <!-- Auction Price Dropdown with Show C&F Price Button -->
            <div class="fv-row mb-4">
              <label class="form-label fw-semibold text-gray-800 required">Bid Type</label>
              <div class="d-flex gap-2">
                <select class="form-select form-select-lg flex-grow-1"
                        formControlName="bidType">
                  <option value="order">Order</option>
                  <option value="stock">Stock</option>
                </select>
                <!-- <button type="button"
                        class="btn btn-outline-primary btn-lg"
                        (click)="showCFPrice()">
                  Show C&F Price
                </button> -->
              </div>
            </div>

            <!-- Price Type Dropdown -->
            <div class="fv-row mb-4">
              <label class="form-label fw-semibold text-gray-800 required">Price Type</label>
              <select class="form-select form-select-lg"
                      [class.is-invalid]="isFieldInvalid('priceType')"
                      formControlName="priceType">
                <option value="">Select Price Type</option>
                <option value="C&F">C&F</option>
                <option value="FOB">FOB</option>
              </select>
              <div *ngIf="isFieldInvalid('priceType')" class="invalid-feedback">
                {{ getFieldError('priceType') }}
              </div>
            </div>

            <!-- Price Type Amount with Currency -->
            <div class="fv-row mb-4">
              <label class="form-label fw-semibold text-gray-800 required">Price Type Amount</label>
              <div class="input-group input-group-lg">
                <input type="number"
                       class="form-control"
                       [class.is-invalid]="isFieldInvalid('priceTypeAmount')"
                       formControlName="priceTypeAmount"
                       placeholder="Enter price type amount"
                       min="0">
                <select class="form-select"
                        style="max-width: 100px;"
                        formControlName="currency">
                  <option value="JPY">JPY</option>
                  <option value="USD">USD</option>
                </select>
              </div>
              <div *ngIf="isFieldInvalid('priceTypeAmount')" class="invalid-feedback">
                {{ getFieldError('priceTypeAmount') }}
              </div>
            </div>

            <!-- Client Search -->
            <div class="fv-row mb-4">
              <label class="form-label fw-semibold text-gray-800">Client</label>
              <div class="position-relative">
                <input type="text"
                       class="form-control form-control-lg"
                       placeholder="Search Client..."
                       [(ngModel)]="clientSearchTerm"
                       [ngModelOptions]="{standalone: true}"
                       [disabled]="isClientFieldDisabled"
                       (input)="onClientSearchInput($event)"
                       (focus)="onClientInputFocus()"
                       (blur)="onClientInputBlur()"
                       autocomplete="off" />

                <!-- Client Search Results Dropdown -->
                <div class="dropdown-menu show w-100 mh-300px overflow-auto"
                     *ngIf="showClientDropdown && !isClientFieldDisabled && (clientSearchResults.length > 0 || isClientSearching)">

                  <!-- Loading State -->
                  <div class="dropdown-item text-center py-3" *ngIf="isClientSearching">
                    <span class="spinner-border spinner-border-sm me-2"></span>
                    Searching clients...
                  </div>

                  <!-- Search Results -->
                  <a class="dropdown-item cursor-pointer py-2"
                     *ngFor="let client of clientSearchResults"
                     (click)="selectClient(client)">
                    <div class="d-flex flex-column">
                      <span class="fw-bold">{{ getClientDisplayName(client) }}</span>
                      <small class="text-muted">{{ client.email }}</small>
                    </div>
                  </a>

                  <!-- No Results -->
                  <div class="dropdown-item text-center py-3 text-muted"
                       *ngIf="!isClientSearching && clientSearchResults.length === 0">
                    No clients found
                  </div>
                </div>
              </div>
            </div>

            <!-- Destination Country -->
            <div class="fv-row mb-4">
              <label class="form-label fw-semibold text-gray-800">Destination Country</label>
              <select class="form-select form-select-lg"
                      formControlName="destinationCountry">
                <option value="">Select Destination Country</option>
                <option *ngFor="let country of countries" [value]="country.code">
                  {{ country.name }}
                </option>
              </select>
            </div>

            <!-- Destination Port -->
            <div class="fv-row mb-4">
              <label class="form-label fw-semibold text-gray-800">Destination Port</label>
              <div class="position-relative">
                <select class="form-select form-select-lg"
                        formControlName="destinationPort"
                        [disabled]="!bidForm.get('destinationCountry')?.value || isLoadingPorts">
                  <option value="">Select Destination Port</option>
                  <option *ngFor="let port of ports" [value]="port._id">
                    {{ port.name }}
                  </option>
                </select>
                <div *ngIf="isLoadingPorts" class="position-absolute top-50 end-0 translate-middle-y me-3">
                  <span class="spinner-border spinner-border-sm text-primary" role="status"></span>
                </div>
              </div>
            </div>

            <!-- C&F Price -->
            <div class="fv-row mb-4" *ngIf="bidForm.get('priceType')?.value === 'C&F'">
              <label class="form-label fw-semibold text-gray-800">C&F Price</label>
              <div class="position-relative">
                <input type="text"
                       class="form-control form-control-lg"
                       [value]="cfPrice !== null ? cfPrice : ''"
                       placeholder="Select port to load C&F price"
                       disabled
                       readonly>
                <div *ngIf="isLoadingCFPrice" class="position-absolute top-50 end-0 translate-middle-y me-3">
                  <span class="spinner-border spinner-border-sm text-primary" role="status"></span>
                </div>
              </div>
            </div>

            <!-- Group Dropdown -->
            <div class="fv-row mb-4">
              <label class="form-label fw-semibold text-gray-800 required">Group</label>
              <select class="form-select form-select-lg" [class.is-invalid]="isFieldInvalid('group')"
                formControlName="group">
                <option value="">Select Group</option>
                <option value="A">Group A</option>
                <option value="B">Group B</option>
                <option value="C">Group C</option>
                <option value="D">Group D</option>
              </select>
              <div *ngIf="isFieldInvalid('group')" class="invalid-feedback">
                {{ getFieldError('group') }}
              </div>
            </div>

            <!-- Number of Units -->
            <div class="fv-row mb-4">
              <label class="form-label fw-semibold text-gray-800">Number of Units</label>
              <div class="position-relative">
                <input type="number"
                       class="form-control form-control-lg"
                       [class.pe-5]="showEditIcon"
                       formControlName="numberOfUnits"
                       placeholder="Select group to load units">
                <div *ngIf="isLoadingBidUnit" class="position-absolute top-50 end-0 translate-middle-y me-3">
                  <span class="spinner-border spinner-border-sm text-primary" role="status"></span>
                </div>
                <button *ngIf="showEditIcon && !isLoadingBidUnit"
                        type="button"
                        class="btn btn-icon btn-sm btn-light-primary position-absolute top-50 end-0 translate-middle-y me-2"
                        (click)="openUpdateBidUnitModal()"
                        title="Edit Bid Unit">
                  <i class="ki-duotone ki-pencil fs-3">
                    <span class="path1"></span>
                    <span class="path2"></span>
                  </i>
                </button>
              </div>
            </div>

            <!-- Message -->
            <div class="fv-row mb-6">
              <label class="form-label fw-semibold text-gray-800">Message (0 / 1000)</label>
              <textarea class="form-control"
                        formControlName="message"
                        rows="4"
                        maxlength="1000"
                        placeholder="Enter your message here..."></textarea>
              <div class="form-text">{{ bidForm.get('message')?.value?.length || 0 }} / 1000 characters</div>
            </div>

            <!-- Submit Button -->
            <button type="submit"
                    class="btn btn-lg w-100"
                    style="background-color: var(--bs-primary); border-color: var(--bs-primary); color: white;"
                    [disabled]="bidForm.invalid || isSubmittingBid">
              <span *ngIf="!isSubmittingBid">Make Bid</span>
              <span *ngIf="isSubmittingBid">
                <span class="spinner-border spinner-border-sm me-2" role="status"></span>
                Submitting...
              </span>
            </button>

          </form>

          <!-- Sold Message -->
          <div *ngIf="vehicleData.is_sold" class="text-center p-4">
            <i class="ki-duotone ki-cross-circle fs-3x text-danger mb-3">
              <span class="path1"></span>
              <span class="path2"></span>
            </i>
            <h5 class="fw-bold text-danger mb-2">Vehicle Sold</h5>
            <p class="text-muted mb-0">This vehicle has already been sold and is no longer available for bidding.</p>
          </div>
          </ng-container>

        </div>
      </div>

      <!-- Send an Inquiry Card -->
      <div class="card mt-4">
        <div class="card-header border-0 pt-6">
          <div class="card-title">
            <h3 class="fw-bold text-gray-900 m-0">
              <i class="ki-duotone ki-message-text-2 fs-2 text-primary me-2">
                <span class="path1"></span>
                <span class="path2"></span>
                <span class="path3"></span>
              </i>
              Send an Inquiry
            </h3>
          </div>
        </div>

        <div class="card-body">
          <!-- Inquiry Form -->
          <form [formGroup]="inquiryForm" (ngSubmit)="onSubmitInquiry()">

            <!-- Message Textarea -->
            <div class="fv-row mb-4">
              <label class="form-label fw-semibold text-gray-800 required">Your Message</label>
              <textarea class="form-control"
                        [class.is-invalid]="isInquiryFieldInvalid('inquiryMessage')"
                        formControlName="inquiryMessage"
                        rows="4"
                        maxlength="1000"
                        placeholder="Enter your inquiry message here..."></textarea>
              <div *ngIf="isInquiryFieldInvalid('inquiryMessage')" class="invalid-feedback">
                {{ getInquiryFieldError('inquiryMessage') }}
              </div>
              <div class="form-text">{{ inquiryForm.get('inquiryMessage')?.value?.length || 0 }} / 1000 characters</div>
            </div>

            <!-- Submit Button -->
            <button type="submit"
                    class="btn btn-lg w-100 btn-light-primary"
                    [disabled]="inquiryForm.invalid || isSubmittingInquiry || !inquiryForm.get('inquiryMessage')?.value?.trim()">
              <span *ngIf="!isSubmittingInquiry">Send Inquiry</span>
              <span *ngIf="isSubmittingInquiry">
                <span class="spinner-border spinner-border-sm me-2" role="status"></span>
                Sending...
              </span>
            </button>

          </form>
        </div>
      </div>

      <!-- Quick Info Card -->


    </div>

  </div>

</div>

<!-- Error State -->
<div *ngIf="!isLoading && !vehicleData" class="d-flex flex-column justify-content-center align-items-center"
  style="min-height: 400px;">
  <i class="ki-duotone ki-cross-circle fs-3x text-danger mb-4">
    <span class="path1"></span>
    <span class="path2"></span>
  </i>
  <h4 class="fw-bold text-gray-900 mb-2">Vehicle Not Found</h4>
  <p class="text-muted mb-4">The requested vehicle could not be found or may have been removed.</p>
  <button type="button" class="btn btn-primary" (click)="goBack()">
    <i class="ki-duotone ki-arrow-left fs-2 me-2">
      <span class="path1"></span>
      <span class="path2"></span>
    </i>
    Back to Search
  </button>
</div>

<!-- Update Bid Unit Modal -->
<app-update-bid-unit (bidUnitUpdated)="onBidUnitUpdated()"></app-update-bid-unit>
